import React, { createContext, useContext, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { csmAiTokens } from '../../common/constant';
import { deriveBrandColors } from '../../common/utils/derive-brand-colors';
import { getContrastYIQ } from '../../common/utils/get-contrast';
import type { BrandingTheme, CSMTheme } from '../../types';
import { useGenerateThemeBranding } from './use-theme-styles';

export const ThemeContext: React.Context<CSMTheme | undefined> = createContext<
	CSMTheme | undefined
>(undefined);

export const useTheme = (): CSMTheme => {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error('useTheme must be used within a ThemeProvider');
	}
	return context;
};

export interface ThemeProviderProps {
	children: React.ReactNode;
	themeConfig: BrandingTheme;
	styles?: React.CSSProperties;
}

/**
 * ThemeProvider component for applying consistent theming to child components.
 *
 * This provider sets up a theme context and injects CSS custom properties for styling its children.
 * It is intended for use by external consumers who want to customize the appearance of UI components
 * according to their brand or product requirements.
 *
 * Usage:
 * ```tsx
 * <ThemeProvider themeConfig={customTheme}>
 *   <YourComponent />
 * </ThemeProvider>
 * ```
 *
 * @param {object} props - The props for ThemeProvider.
 * @param {React.ReactNode} props.children - The child elements that will receive the theme context and styles.
 * @param {BrandingTheme} [props.themeConfig] - Optional custom theme configuration. If omitted, a default theme is used.
 *   - primaryColor: The main brand color (string, e.g., '#0052CC').
 *   - colorVibe: Controls the color palette and contrast ('light' | 'dark' | 'vibrant' | 'leadershipCircle').
 *   - radius: Controls the corner roundness ('sharp' | 'normal' | 'rounded').
 *   - space: Determines the spacing density ('cosy' | 'comfortable' | 'generous').
 * @param {React.CSSProperties} [props.styles] - Optional styles to apply to the root element.
 * @returns {JSX.Element} Themed context provider wrapping the children.
 */
export const ThemeProvider = ({
	children,
	themeConfig,
	styles = {},
}: ThemeProviderProps): JSX.Element => {
	if (!themeConfig) {
		throw new Error('themeConfig is required for the CSM Branding to works');
	}

	const themeStyles = useGenerateThemeBranding(themeConfig);

	const tokenStyles = useMemo(() => {
		const { borderRadius, space, colorRamps } = themeStyles;

		const iconColor = getContrastYIQ(themeConfig.primaryColor) === 'dark' ? '#FFFFFF' : '#000000';
		const brandColor = deriveBrandColors(colorRamps, themeConfig.primaryColor);

		const properties = {
			[csmAiTokens['color.100'] as string]: colorRamps.color100,
			[csmAiTokens['color.200'] as string]: colorRamps.color200,
			[csmAiTokens['color.300'] as string]: colorRamps.color300,
			[csmAiTokens['color.400'] as string]: colorRamps.color400,
			[csmAiTokens['color.500'] as string]: colorRamps.color500,
			[csmAiTokens['color.600'] as string]: colorRamps.color600,
			[csmAiTokens['color.700'] as string]: colorRamps.color700,
			[csmAiTokens['color.800'] as string]: colorRamps.color800,
			[csmAiTokens['color.900'] as string]: colorRamps.color900,
			[csmAiTokens['color.1000'] as string]: colorRamps.color1000,
			[csmAiTokens['color.icon'] as string]: iconColor,
			[csmAiTokens['color.brand'] as string]: brandColor.brand,
			[csmAiTokens['color.brand.hover'] as string]: brandColor.hover,
			[csmAiTokens['color.brand.active'] as string]: brandColor.active,
			[csmAiTokens['color.brand.text'] as string]: brandColor.brandText,
			[csmAiTokens['color.brand.hover.text'] as string]: brandColor.hoverText,
			[csmAiTokens['color.brand.active.text'] as string]: brandColor.activeText,
			[csmAiTokens['border.radius.lg'] as string]: borderRadius.lg,
			[csmAiTokens['border.radius.md'] as string]: borderRadius.md,
			[csmAiTokens['border.radius.sm'] as string]: borderRadius.sm,
			[csmAiTokens['border.radius.container'] as string]: borderRadius.container,
			[csmAiTokens['border.radius.widget'] as string]: borderRadius.widget,
			[csmAiTokens['space.lg'] as string]: space.lg,
			[csmAiTokens['space.md'] as string]: space.md,
			[csmAiTokens['space.sm'] as string]: space.sm,
		} satisfies React.CSSProperties;

		return properties;
	}, [themeConfig.primaryColor, themeStyles]);

	const combineStyles = {
		...tokenStyles,
		...styles,
		// Prefer contents so this wrapper only supplies CSS variables and does
		// not participate in layout. Fall back to inline while the gate is off.
		display: fg('csm-theme-wrapper-display-contents') ? 'contents' : 'inline',
	};

	return (
		<ThemeContext.Provider value={themeStyles}>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
			<div style={combineStyles}>{children}</div>
		</ThemeContext.Provider>
	);
};
